import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Dashboard = ({ tenantName }) => {
    const { api } = useAuth();
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageTitle, setPageTitle] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userInfo, setUserInfo] = useState(null);

    // Debug: Check what we got from useAuth
    console.log('Dashboard: api from useAuth:', api);
    console.log('Dashboard: api.get function:', api?.get);
    console.log('Dashboard: typeof api:', typeof api);

    const fetchUserRole = async () => {
        try {
            const response = await api.get('/api/user-role/');
            console.log('User role response:', response.data);
            setUserRole(response.data.role);
            setUserInfo(response.data.user);
        } catch (error) {
            console.error("Failed to fetch user role:", error);
            setError(`Failed to fetch user role: ${error.response?.data?.detail || error.message}`);
        }
    };

    const fetchPages = async () => {
        try {
            console.log('Fetching pages...');
            console.log('fetchPages: api object:', api);
            console.log('fetchPages: api.get function:', api?.get);
            
            if (!api || typeof api.get !== 'function') {
                throw new Error('API object is not properly initialized');
            }
            
            const response = await api.get('/api/pages/');
            console.log('Pages response:', response.data);
            setPages(response.data);
            setError(null);
        } catch (error) {
            console.error("Failed to fetch pages:", error);
            setError(`Failed to fetch pages: ${error.response?.data?.detail || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserRole();
        fetchPages();
    }, [api]);

    const handleCreatePage = async (e) => {
        e.preventDefault();
        try {
            console.log('Creating page with title:', pageTitle);
            const response = await api.post('/api/pages/', { title: pageTitle });
            console.log('Create page response:', response.data);
            setPages([...pages, response.data]);
            setPageTitle('');
            setIsModalOpen(false);
            setError(null);
        } catch (error) {
            console.error("Failed to create page:", error);
            setError(`Failed to create page: ${error.response?.data?.detail || error.message}`);
        }
    };

    const handleDebugAuth = async () => {
        try {
            console.log('Testing debug auth...');
            const response = await api.get('/api/debug-auth/');
            console.log('Debug auth response:', response.data);
            alert(JSON.stringify(response.data, null, 2));
        } catch (error) {
            console.error("Debug auth failed:", error);
            alert(`Debug auth failed: ${error.response?.data?.detail || error.message}`);
        }
    };

    const handleTestAuth = async () => {
        try {
            console.log('Testing simple auth...');
            const response = await api.get('/api/test-auth/');
            console.log('Test auth response:', response.data);
            alert(JSON.stringify(response.data, null, 2));
        } catch (error) {
            console.error("Test auth failed:", error);
            alert(`Test auth failed: ${error.response?.data?.detail || error.message}`);
        }
    };

    if (loading) return <p>Loading dashboard...</p>;

    const isAdmin = userRole === 'Admin' || userRole === 'superuser';
    const isMember = userRole === 'Resident';

    return (
        <div className="min-h-screen bg-gray-100">
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 capitalize">{tenantName} Dashboard</h1>
                            {userInfo && (
                                <p className="text-sm text-gray-600 mt-1">
                                    Welcome, {userInfo.first_name || userInfo.username} 
                                    {userRole && ` (${userRole})`}
                                </p>
                            )}
                        </div>
                        <div className="space-x-2">
                            {isAdmin && (
                                <Link
                                    to="/members"
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    👥 Member Access
                                </Link>
                            )}
                            <button onClick={handleDebugAuth} className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600">
                                Debug Auth
                            </button>
                            <button onClick={handleTestAuth} className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600">
                                Test Auth
                            </button>
                        </div>
                    </div>
                </div>
            </header>
            <main>
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    {error && (
                        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                            <strong>Error:</strong> {error}
                        </div>
                    )}
                    
                    {/* Role-based welcome message */}
                    <div className="mb-6">
                        {isAdmin && (
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                <h3 className="text-lg font-medium text-blue-900">Admin Dashboard</h3>
                                <p className="text-blue-700 mt-1">
                                    You have full access to create, edit, and manage pages and forms for your society.
                                </p>
                            </div>
                        )}
                        {isMember && (
                            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                                <h3 className="text-lg font-medium text-green-900">Member Dashboard</h3>
                                <p className="text-green-700 mt-1">
                                    You can view and interact with pages and forms. Contact an admin to create new content.
                                </p>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold">Society Pages</h2>
                        {isAdmin && (
                            <button 
                                onClick={() => setIsModalOpen(true)} 
                                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                            >
                                Create New Page
                            </button>
                        )}
                    </div>

                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        {pages && pages.length > 0 ? (
                            <ul className="divide-y divide-gray-200">
                                {pages.map(page => (
                                    <li key={page.id}>
                                        <Link to={`/page/${page.id}`} className="block hover:bg-gray-50">
                                            <div className="px-4 py-4 sm:px-6">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-sm font-medium text-indigo-600 truncate">{page.title}</p>
                                                        <p className="text-sm text-gray-500 mt-1">
                                                            {page.forms?.length || 0} forms available
                                                        </p>
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {isAdmin ? (
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                Admin Access
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                View Only
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="p-8 text-center">
                                <p className="text-gray-500 text-lg">No pages available yet.</p>
                                {isAdmin && (
                                    <p className="text-gray-400 mt-2">Click "Create New Page" to get started.</p>
                                )}
                                {isMember && (
                                    <p className="text-gray-400 mt-2">Contact an admin to create pages and forms.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Create Page Modal - Only for Admins */}
            {isModalOpen && isAdmin && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
                    <div className="bg-white p-5 rounded-lg shadow-xl">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Create a New Page</h3>
                        <form onSubmit={handleCreatePage} className="mt-2">
                            <input 
                                type="text" 
                                value={pageTitle} 
                                onChange={(e) => setPageTitle(e.target.value)}
                                placeholder="Enter page title (e.g., Clubhouse Booking)" 
                                required
                                className="w-full p-2 border border-gray-300 rounded-md" 
                            />
                            <div className="mt-4 flex justify-end space-x-2">
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)} 
                                    className="px-4 py-2 bg-gray-200 rounded-md"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md"
                                >
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
